import React, { useCallback } from "react";
import DOMPurify from "dompurify";
import { Box, Button } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    StopRounded as StopIcon,
    VolumeUpRounded as VolumeUpIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { pageBackgroundSx } from "./interviewTheme";
import {
    ContentSheet,
    QuestionHead,
    ChoiceRow,
    ScaleRow,
    PrimaryPill,
    backButtonSx,
    formCardSx,
} from "./RespondentFormKit";
import { contentText, isContent, missingRequired, questionText } from "./voiceSurveyFlow";

// One page of a voice-mode survey (see buildVoiceStages): its choice / Likert
// questions, answered on screen in the interview's look, and its content blocks,
// each with a Listen button that reads it aloud (never on its own). Presentational:
// the answers and the voice live in the runner. `items` is the page in order,
// `questions` its answerable part; `answers` is { questionId: optionId | [optionId] }.
// Continue stays off until every required question on the page has an answer.
export default function VoiceSurveyFormScreen({
    items,
    questions,
    answers,
    onChange,
    onContinue,
    busy,
    numberFrom = 1,
    readingId = null,
    onReadAloud,
    onStopReading,
    continueLabel, // the survey's own Next button text
    onBack, // unset on the survey's first item
    welcome = null, // the survey's greeting, on its very first page
    backLabel, // the survey's own Back button text
    t,
}) {
    const missing = missingRequired(questions, answers);

    const handleContinue = useCallback(() => {
        if (busy || missing.length > 0) return;
        onContinue();
    }, [busy, missing, onContinue]);

    return (
        <Box sx={{ minHeight: t.screenMinHeight || "100vh", flexGrow: 1, ...pageBackgroundSx(t), display: "flex", flexDirection: "column" }}>
            <ContentSheet t={t} maxWidth={760}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", px: { xs: 2, sm: 3.75 }, pt: { xs: 2, sm: 2.5 }, pb: 3 }}>
                    {welcome}
                    <Box sx={{ width: "100%", maxWidth: 560 }}>
                        {(items || questions).map((q) => {
                            if (isContent(q)) {
                                return (
                                    <ContentBlock
                                        key={q.id}
                                        step={q}
                                        reading={readingId === q.id}
                                        onReadAloud={onReadAloud}
                                        onStopReading={onStopReading}
                                        t={t}
                                    />
                                );
                            }
                            const i = questions.indexOf(q);
                            const labelId = `voice-form-q-${q.id}`;
                            const value = answers[q.id];
                            const multi = q.type === "MULTI_CHOICE";
                            const answered = multi ? Array.isArray(value) && value.length > 0 : value != null;
                            return (
                                <Box key={q.id} sx={formCardSx(t)}>
                                    <QuestionHead
                                        t={t}
                                        n={t.showQuestionNumber === false ? null : numberFrom + i}
                                        text={questionText(q)}
                                        id={labelId}
                                        serif={q.type === "LIKERT_SCALE"}
                                        required={!!q.is_required}
                                        answered={answered}
                                    />
                                    <ChoiceAnswer q={q} value={value} onChange={onChange} labelId={labelId} t={t} />
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ width: "100%", maxWidth: 560, display: "flex", alignItems: "center", justifyContent: onBack ? "space-between" : "center", gap: 1.5 }}>
                        {onBack && (
                            <Button
                                onClick={onBack}
                                disabled={busy}
                                startIcon={<ArrowBackIcon />}
                                data-testid="voice-back"
                                sx={backButtonSx(t)}
                            >
                                {backLabel || <FormattedMessage id="voice_survey.back" defaultMessage="Back" />}
                            </Button>
                        )}
                        <PrimaryPill t={t} onClick={handleContinue} busy={busy} disabled={missing.length > 0} endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}>
                            {continueLabel || <FormattedMessage id="voice_survey.continue" defaultMessage="Continue" />}
                        </PrimaryPill>
                    </Box>
                </Box>
            </ContentSheet>
        </Box>
    );
}

// A content-only block: its text, and a Listen / Stop toggle. Reading starts only
// on Listen and runs to the end unless Stop (or another block's Listen) cuts it.
function ContentBlock({ step, reading, onReadAloud, onStopReading, t }) {
    const intl = useIntl();
    const text = contentText(step);
    const html = DOMPurify.sanitize(step.question || "");
    const description = step.description && step.description !== "<p><br></p>" ? DOMPurify.sanitize(step.description) : "";
    return (
        <Box sx={formCardSx(t)} data-testid={`voice-content-${step.id}`}>
            <Box
                sx={{ color: t.formLabel, lineHeight: 1.65, fontSize: "0.98rem", "& p": { m: 0, mb: 1 }, "& p:last-child": { mb: 0 }, wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
            {description && (
                <Box sx={{ mt: 1, color: t.formMuted, fontSize: "0.9rem", lineHeight: 1.6, "& p": { m: 0 } }} dangerouslySetInnerHTML={{ __html: description }} />
            )}
            {text && onReadAloud && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                    <Button
                        size="small"
                        onClick={() => (reading ? onStopReading?.() : onReadAloud(step.id, text))}
                        aria-pressed={reading}
                        startIcon={reading ? <StopIcon /> : <VolumeUpIcon />}
                        sx={{
                            borderRadius: 999,
                            px: 1.75,
                            textTransform: "none",
                            fontWeight: 600,
                            color: t.cueText,
                            backgroundColor: t.cueBg,
                            "&:hover": { backgroundColor: t.cueBg, filter: "brightness(0.95)" },
                        }}
                    >
                        {reading
                            ? intl.formatMessage({ id: "voice_survey.stop_reading", defaultMessage: "Stop" })
                            : intl.formatMessage({ id: "voice_survey.read_aloud", defaultMessage: "Listen" })}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

// A choice or Likert question's options, answered in place: on its page, and
// again on the review. `value` is an option id, or a list of them (multi choice).
export function ChoiceAnswer({ q, value, onChange, labelId, t }) {
    const multi = q.type === "MULTI_CHOICE";
    const toggle = (optionId) => {
        const current = Array.isArray(value) ? value : [];
        onChange(q.id, current.includes(optionId) ? current.filter((id) => id !== optionId) : [...current, optionId]);
    };
    if (q.type === "LIKERT_SCALE") {
        return (
            <ScaleRow
                t={t}
                name={`voice-scale-${q.id}`}
                labelledBy={labelId}
                required={!!q.is_required}
                options={(q.choices || []).map((c) => ({ key: String(c.id), value: c.id, label: c.text }))}
                selectedValue={value}
                onSelect={(optionId) => onChange(q.id, optionId)}
            />
        );
    }
    return (
        <Box
            role={multi ? "group" : "radiogroup"}
            aria-labelledby={labelId}
            aria-required={q.is_required || undefined}
            sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
            {(q.choices || []).map((c) => (
                <ChoiceRow
                    key={c.id}
                    t={t}
                    multi={multi}
                    name={`voice-choice-${q.id}`}
                    label={c.text}
                    selected={multi ? Array.isArray(value) && value.includes(c.id) : value === c.id}
                    onSelect={() => (multi ? toggle(c.id) : onChange(q.id, c.id))}
                />
            ))}
        </Box>
    );
}
